import { CSSProperties, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { clsx } from 'modules/Utils';
import { NavigationItem } from './Navbar';
import symphonyLogo from '../../assets/images/symphonyLogo.svg';
import Colors from 'modules/Colors';
import { getNavigationPlaceholderImage } from 'modules/Const';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';

interface PublicMobileNavigationProps {
    className: string;
    navigationItems: NavigationItem[];
}

const PublicMobileNavigation = ({
    className,
    navigationItems,
}: PublicMobileNavigationProps) => {
    const history = useHistory();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [expandedDropdown, setExpandedDropdown] = useState<string | null>(null);

    // Filter navigation items for public mobile menu
    const publicNavigationItems = navigationItems.filter(
        (navItem: NavigationItem) =>
            navItem.isPublic || navItem.isAvailableAlways
    );

    const handleLogoClick = () => {
        history.push('/');
        setIsMenuOpen(false);
    };

    const handleNavItemClick = (navItem: NavigationItem) => {
        if (navItem.dropdownItems && navItem.dropdownItems.length > 0) {
            setExpandedDropdown(expandedDropdown === navItem.name ? null : navItem.name);
        } else {
            if (navItem.onClick) {
                navItem.onClick({} as any);
            } else if (navItem.href) {
                history.push(navItem.href);
            }
            setIsMenuOpen(false);
            setExpandedDropdown(null);
        }
    };

    const handleDropdownItemClick = (dropdownItem: NavigationItem) => {
        if (dropdownItem.onClick) {
            dropdownItem.onClick({} as any);
        } else if (dropdownItem.href) {
            history.push(dropdownItem.href);
        }
        setIsMenuOpen(false);
        setExpandedDropdown(null);
    };



    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        if (!isMenuOpen) {
            setExpandedDropdown(null);
        }
    };

    const renderHeader = () => {
        return (
            <div className="publicMobileNavigationHeader" style={sty.publicMobileNavigationHeader}>
                <div
                    className="publicMobileNavigationLogo"
                    style={sty.logo}
                    onClick={handleLogoClick}
                >
                    <img
                        className="symphonyLogoPublicMobile"
                        src={symphonyLogo}
                        alt="Symphony Logo"
                        style={sty.logoImage}
                    />
                </div>
                <Icon
                    className="publicMobileNavigationMenuIcon"
                    icon={isMenuOpen ? 'close' : 'menu'}
                    color={Colors.white}
                    size={24}
                    onClick={toggleMenu}
                />
            </div>
        );
    };

    const renderMobileMenu = () => {
        // Get all public navigation items for mobile menu
        const mobileMenuItems = publicNavigationItems.filter(
            item => (item.isPublic || item.isAvailableAlways) && item.showOnMobileMainMenu
        );

        return (
            <div
                className="publicMobileNavigationMenu"
                style={{
                    ...sty.menu,
                    maxHeight: isMenuOpen ? '600px' : '0px',
                    opacity: isMenuOpen ? 1 : 0,
                    transform: isMenuOpen ? 'translateY(0)' : 'translateY(-10px)',
                    transition: 'all 0.3s ease-in-out',
                    overflow: 'hidden',
                }}
            >
                {mobileMenuItems.map((navItem: NavigationItem, index: number) => {
                    const hasDropdown = navItem.dropdownItems && navItem.dropdownItems.length > 0;
                    const isExpanded = expandedDropdown === navItem.name;

                    return (
                        <div key={navItem.name}>
                            <div
                                className="publicMobileNavigationMenuItem"
                                style={{
                                    ...sty.menuItem,
                                    opacity: isMenuOpen ? 1 : 0,
                                    transform: isMenuOpen ? 'translateX(0)' : 'translateX(-20px)',
                                    transition: `all 0.3s ease-in-out ${index * 0.05}s`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                                onClick={() => handleNavItemClick(navItem)}
                            >
                                {navItem.name}
                                {hasDropdown && (
                                    <Icon
                                        className="publicMobileNavigationDropdownIcon"
                                        icon="chevronDown"
                                        size={16}
                                        color={Colors.white}
                                        style={{
                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease',
                                        }}
                                    />
                                )}
                            </div>
                            {hasDropdown && isExpanded && (
                                <div className="publicMobileNavigationDropdown" style={sty.mobileDropdown}>
                                    {navItem.name === 'Learn' ? (
                                        // Simple links for Learn dropdown
                                        <div className="publicMobileNavigationSimpleDropdown" style={sty.mobileSimpleDropdown}>
                                            {navItem.dropdownItems!.map((dropdownItem: NavigationItem, dropdownIndex: number) => (
                                                <div
                                                    key={dropdownItem.name}
                                                    className="publicMobileNavigationSimpleDropdownItem"
                                                    style={{
                                                        ...sty.mobileSimpleDropdownItem,
                                                        opacity: isExpanded ? 1 : 0,
                                                        transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
                                                        transition: `all 0.3s ease-in-out ${dropdownIndex * 0.1}s`,
                                                    }}
                                                    onClick={() => handleDropdownItemClick(dropdownItem)}
                                                >
                                                    {dropdownItem.name}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        // Box-style dropdown for Platform and other items
                                        <div className="publicMobileNavigationDropdownGrid" style={sty.mobileDropdownGrid}>
                                            {navItem.dropdownItems!.map((dropdownItem: NavigationItem, dropdownIndex: number) => (
                                                <div
                                                    key={dropdownItem.name}
                                                    className="publicMobileNavigationDropdownItem"
                                                    style={{
                                                        ...sty.mobileDropdownItem,
                                                        opacity: isExpanded ? 1 : 0,
                                                        transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
                                                        transition: `all 0.3s ease-in-out ${dropdownIndex * 0.1}s`,
                                                    }}
                                                    onClick={() => handleDropdownItemClick(dropdownItem)}
                                                >
                                                    <div className="mobileDropdownItemImageContainer" style={sty.mobileDropdownItemImageContainer}>
                                                        <img
                                                            src={getNavigationPlaceholderImage(dropdownItem.name, 'mobile')}
                                                            alt={dropdownItem.name}
                                                            style={sty.mobileDropdownItemImage}
                                                        />
                                                    </div>
                                                    <div className="mobileDropdownItemTitle" style={sty.mobileDropdownItemTitle}>
                                                        {dropdownItem.name}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div
            className={clsx('publicMobileNavigationComponent', className)}
            style={sty.container}
        >
            {renderHeader()}
            {renderMobileMenu()}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    container: {
        backgroundColor: Colors.purpleDark,
        borderBottom: `1px solid ${Colors.greyDark}`,
        position: 'relative',
    },
    publicMobileNavigationHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
    },
    logo: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    logoImage: {
        height: 18,
        width: 'auto',
    },
    menu: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: Colors.purpleDark,
        borderBottom: `1px solid ${Colors.greyDark}`,
        zIndex: 1000,
    },
    menuItem: {
        padding: '16px 20px',
        color: Colors.white,
        fontSize: 16,
        fontWeight: 500,
        cursor: 'pointer',
        borderBottom: `1px solid ${Colors.greyDark}`,
    },
    mobileDropdown: {
        backgroundColor: Colors.white,
        padding: 16,
        borderBottom: `1px solid ${Colors.greyDark}`,
    },
    mobileDropdownGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 12,
    },
    mobileDropdownItem: {
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${Colors.greyLight}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: Colors.white,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: 120,
    },
    mobileDropdownItemImageContainer: {
        marginBottom: 8,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    mobileDropdownItemImage: {
        width: 60,
        height: 45,
        objectFit: 'cover',
        borderRadius: 4,
    },
    mobileDropdownItemTitle: {
        fontSize: 12,
        fontWeight: 500,
        color: Colors.grey,
        lineHeight: 1.3,
    },
    mobileSimpleDropdown: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
    },
    mobileSimpleDropdownItem: {
        padding: '12px 16px',
        color: Colors.grey,
        fontSize: 14,
        fontWeight: 400,
        cursor: 'pointer',
        borderBottom: `1px solid ${Colors.greyLight}`,
        transition: 'all 0.2s ease',
        textAlign: 'left',
    },
};

export default PublicMobileNavigation; 